import React, { useEffect, useState } from 'react';

import { AppState } from '@excalidraw/excalidraw/types/types';
import { ZoomIn, ZoomOut } from '@mui/icons-material';
import { Button, TextField, InputAdornment, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { NormalizedZoomValue } from '../../types';

export const getNormalizedZoom = (zoom: number): NormalizedZoomValue => Math.max(0.1, Math.min(zoom, 30)) as NormalizedZoomValue;

export const getStateForZoom = (
    {
        viewportX,
        viewportY,
        nextZoom
    }: {
        viewportX: number;
        viewportY: number;
        nextZoom: NormalizedZoomValue;
    },
    appState: AppState
) => {
    const appLayerX = viewportX - appState.offsetLeft;
    const appLayerY = viewportY - appState.offsetTop;

    const currentZoom = appState.zoom.value;

    // get original scroll position without zoom
    const baseScrollX = appState.scrollX + (appLayerX - appLayerX / currentZoom);
    const baseScrollY = appState.scrollY + (appLayerY - appLayerY / currentZoom);

    // get scroll offsets for target zoom level
    const zoomOffsetScrollX = -(appLayerX - appLayerX / nextZoom);
    const zoomOffsetScrollY = -(appLayerY - appLayerY / nextZoom);

    return {
        scrollX: baseScrollX + zoomOffsetScrollX,
        scrollY: baseScrollY + zoomOffsetScrollY,
        zoom: {
            value: nextZoom
        }
    };
};

type Props = {
    excalidrawZoom: number;
    onZoomChange: (newZoom: number) => void;
};

const ZOOM_STEP = 0.1;

const ZoomTools = React.memo((props: Props) => {
    const { t } = useTranslation(['whiteboard', 'common']);

    const [naturalZoomValue, setNaturalZoomValue] = useState<number | ''>(props.excalidrawZoom * 100);

    useEffect(() => {
        setNaturalZoomValue(parseFloat((props.excalidrawZoom * 100).toFixed(0)));
    }, [props.excalidrawZoom]);

    const setExcalidrawZoom = (value: number) => {
        setNaturalZoomValue(Math.round(value * 100));
        props.onZoomChange(value);
    };

    const handleZoomButtonClick = (step: number) => {
        setExcalidrawZoom(parseFloat(getNormalizedZoom(props.excalidrawZoom + step).toFixed(1)));
    };

    return (
        <>
            <Grid item>
                <Button
                    title={t('whiteboard:buttons.zoomIn')}
                    style={{ minWidth: 0, padding: '0.45rem', margin: '1.5px' }}
                    variant="contained"
                    disableElevation
                    onClick={() => handleZoomButtonClick(ZOOM_STEP)}
                    disabled={naturalZoomValue >= 1000}
                    color="inherit"
                >
                    <ZoomIn/>
                </Button>
            </Grid>
            <Grid item>
                <Button
                    title={t('whiteboard:buttons.zoomOut')}
                    style={{ minWidth: 0, padding: '0.45rem', margin: '1.5px' }}
                    variant="contained"
                    disableElevation
                    onClick={() => handleZoomButtonClick(-ZOOM_STEP)}
                    disabled={naturalZoomValue <= 10}
                    color="inherit"
                >
                    <ZoomOut/>
                </Button>
            </Grid>
            <Grid item>
                <TextField
                    value={naturalZoomValue}
                    hiddenLabel
                    variant="outlined"
                    size="small"
                    style={{ marginTop: '2px', width: '4.5rem' }}
                    InputProps={{
                        sx: { maxHeight: '38px' },
                        endAdornment: <InputAdornment sx={{ width: '0.25rem' }} position="end">%</InputAdornment>
                    }}
                    onChange={(event) => {
                        const newNaturalZoomValue = parseInt(event.target.value, 10);
                        if (Number.isInteger(newNaturalZoomValue)) {
                            setNaturalZoomValue(newNaturalZoomValue);
                            const newExcalidrawZoom = newNaturalZoomValue / 100;
                            if (newExcalidrawZoom === getNormalizedZoom(newExcalidrawZoom)) {
                                setExcalidrawZoom(newExcalidrawZoom);
                            }
                        } else {
                            setNaturalZoomValue('');
                        }
                    }}
                />
            </Grid>
        </>
    );
});

export default ZoomTools;
